/**
 * Artikel erfassen (Mieterportal 2.0): drei Schritte – 1 Foto (Kamera zuerst, Zuschnitt, Drehen, Helligkeit, Bewertung),
 * 2 Bezeichnung + Preis (Chips, Vorlagen, Preisspanne aus Ladenverkäufen, Varianten), 3 Fertig (Nächster, Etikett, Packliste).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Button, Icon, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { Select } from "../ui/Layout";
import { ApiFehler } from "../api/client";
import { formatEuro, parseEuro } from "@shared/geld";
import { fotoBearbeiten, fotoBewerten, fotoVerkleinern, type Bearbeitung } from "./foto";
import { useArtikelAnlegen, useErfassenInfo, useFotoHochladen, type ArtikelZeile } from "./artikel-api";
import { useCockpit, usePortalSpeichern, usePreisspanne } from "./cockpit-api";
import type { Vorlage } from "@shared/schemas/mieterportal";
import { useT, useTx } from "../i18n";
import { istNetzfehler, offlineMerken } from "./offline";

export function PreisChips({ chips, wertCent, onWahl }: { chips: number[]; wertCent: number | null; onWahl: (cent: number) => void }) {
  return (
    <div className="rg-chips" role="group" aria-label="Preis schnell wählen">
      {chips.map((c) => (
        <button key={c} type="button" className={["rg-chip", wertCent === c && "is-on"].filter(Boolean).join(" ")} onClick={() => onWahl(c)}>
          {c % 100 === 0 ? `${c / 100} €` : formatEuro(c)}
        </button>
      ))}
    </div>
  );
}

export function preisHinweis(cent: number | null, min: number, max: number): string | null {
  if (cent === null) return null;
  if (cent < min) return `${formatEuro(cent)} – ist der Preis richtig? Üblich sind mindestens ${formatEuro(min)}.`;
  if (cent > max) return `${formatEuro(cent)} – ist der Preis richtig? Über ${formatEuro(max)} ist ungewöhnlich.`;
  return null;
}

/** Foto-Feld (Artikel bearbeiten): Kamera/Galerie, Vorschau, Verkleinern vor dem Upload. */
export function FotoFeld({ vorschau, onDatei, onEntfernen, laeuft }: { vorschau: string | null; onDatei: (b: Blob, url: string) => void; onEntfernen?: () => void; laeuft?: boolean }) {
  const tx = useTx();
  const input = useRef<HTMLInputElement>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  async function gewaehlt(f: File | undefined) {
    if (!f) return;
    setFehler(null);
    try {
      const blob = await fotoVerkleinern(f);
      onDatei(blob, URL.createObjectURL(blob));
    } catch (err) {
      setFehler(err instanceof Error ? err.message : "Foto konnte nicht gelesen werden.");
    }
  }
  return (
    <div className="sc-col" style={{ gap: 6 }}>
      <span className="rg-field__label">Foto {vorschau ? "" : <span className="sc-muted">(freiwillig, hilft an der Kasse)</span>}</span>
      <div className="sc-row">
        <button type="button" className="mp-foto" onClick={() => input.current?.click()} aria-label={vorschau ? "Foto ändern" : "Foto aufnehmen"}>
          {vorschau ? <img src={vorschau} alt="" /> : <Icon name="camera" size={28} />}
        </button>
        <div className="sc-col" style={{ gap: 6 }}>
          <Button variant="secondary" icon="camera" loading={laeuft} onClick={() => input.current?.click()}>{vorschau ? "Anderes Foto" : "Foto aufnehmen"}</Button>
          {vorschau && onEntfernen ? <Button variant="quiet" icon="x" onClick={onEntfernen}>{tx("Entfernen")}</Button> : null}
        </div>
      </div>
      {fehler ? <Notice tone="error">{fehler}</Notice> : null}
      <input ref={input} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { void gewaehlt(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
  );
}

/** Preisspanne-Balken: wo liegt der Preis im Vergleich zu verkauften Artikeln der Kategorie. */
function Preisspanne({ kategorie, wort, preisCent, onVorschlag }: { kategorie: string; wort: string; preisCent: number | null; onVorschlag: (c: number) => void }) {
  const q = usePreisspanne(kategorie, wort);
  const s = q.data?.spanne;
  if (!s) return null;
  const pos = preisCent === null ? null : Math.max(0, Math.min(100, ((preisCent - s.minCent) / Math.max(1, s.maxCent - s.minCent)) * 100));
  return (
    <div className="sc-col" style={{ gap: 2 }}>
      <span className="sc-small">Hier verkauft: <span className="sc-strong">{formatEuro(s.minCent)} – {formatEuro(s.maxCent)}</span> <span className="sc-muted">({s.anzahl} {kategorie ? kategorie : "ähnliche"}, meist {formatEuro(s.medianCent)})</span></span>
      <div className="mp-spanne" aria-hidden="true">{pos !== null ? <i style={{ left: `${pos}%` }} /> : null}</div>
      {preisCent === null || Math.abs(preisCent - s.medianCent) > s.medianCent * 0.3 ? <button type="button" className="rg-btn rg-btn--quiet" style={{ alignSelf: "flex-start", paddingLeft: 0 }} onClick={() => onVorschlag(s.medianCent)}>Vorschlag übernehmen: {formatEuro(s.medianCent)}</button> : null}
    </div>
  );
}

const GROESSEN = ["XS", "S", "M", "L", "XL"];

export function Erfassen() {
  const tx = useTx();
  const info = useErfassenInfo();
  const ck = useCockpit();
  const anlegen = useArtikelAnlegen();
  const t = useT();
  const foto = useFotoHochladen();
  const portal = usePortalSpeichern();
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const galerie = useRef<HTMLInputElement>(null);
  const [schritt, setSchritt] = useState<1 | 2 | 3>(1);
  const [original, setOriginal] = useState<Blob | null>(null);
  const [bearb, setBearb] = useState<Bearbeitung>({ quadrat: true, drehung: 0, helligkeit: 1 });
  const [vorschau, setVorschau] = useState<{ blob: Blob; url: string } | null>(null);
  const [bewertung, setBewertung] = useState<{ dunkel: boolean; unscharf: boolean } | null>(null);
  const [regalId, setRegalId] = useState("");
  const [bezeichnung, setBezeichnung] = useState("");
  const [preis, setPreis] = useState("");
  const [kategorie, setKategorie] = useState("");
  const [groesse, setGroesse] = useState("");
  const [varianten, setVarianten] = useState<string[]>([]);
  const [mehr, setMehr] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [angelegt, setAngelegt] = useState<ArtikelZeile[]>([]);
  const [heuteZaehler, setHeuteZaehler] = useState(0);
  const [offlineHinweis, setOfflineHinweis] = useState(false);
  const preisCent = parseEuro(preis);
  const einst = ck.data?.einstellungen;
  const vorlagen = einst?.vorlagen ?? [];
  const kategorien = useMemo(() => [...new Set([...(einst?.kategorien ?? []), ...(ck.data?.statistik.bestseller.map((b) => b.kategorie).filter((k) => k !== "Ohne Kategorie") ?? [])])].slice(0, 8), [einst, ck.data]);

  useEffect(() => {
    if (info.data && !regalId && info.data.regale.length) setRegalId(info.data.regale[0]!.regalId);
  }, [info.data, regalId]);

  // Foto neu rendern, wenn Zuschnitt/Drehung/Helligkeit sich ändern
  useEffect(() => {
    if (!original) return;
    let aktiv = true;
    void fotoBearbeiten(original, bearb).then((b) => {
      if (!aktiv) return;
      setVorschau((alt) => {
        if (alt) URL.revokeObjectURL(alt.url);
        return { blob: b, url: URL.createObjectURL(b) };
      });
    });
    return () => {
      aktiv = false;
    };
  }, [original, bearb]);

  async function fotoGewaehlt(f: File | undefined) {
    if (!f) return;
    setFehler(null);
    try {
      const klein = await fotoVerkleinern(f, 1600);
      setOriginal(klein);
      setBearb({ quadrat: true, drehung: 0, helligkeit: 1, versatzX: 0, versatzY: 0 });
      const bw = await fotoBewerten(klein);
      setBewertung({ dunkel: bw.dunkel, unscharf: bw.unscharf });
      if (bw.dunkel) setBearb((b) => ({ ...b, helligkeit: 1.25 }));
      setSchritt(2);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : "Foto konnte nicht gelesen werden.");
    }
  }

  function vorlageNutzen(v: Vorlage) {
    setBezeichnung(v.bezeichnung);
    setPreis((v.preisCent / 100).toFixed(2).replace(".", ","));
    setKategorie(v.kategorie);
  }

  async function speichern(alsVorlage = false) {
    setFehler(null);
    if (!bezeichnung.trim()) return setFehler("Bitte eine Bezeichnung eingeben.");
    if (!preisCent || preisCent <= 0) return setFehler("Bitte einen Preis über 0,00 € eingeben.");
    const anzahl = varianten.length ? varianten.length : 1;
    const bezeichnungen = varianten.length ? varianten.map((v) => `${bezeichnung.trim()}, Gr. ${v}`) : undefined;
    try {
      const r = await anlegen.mutateAsync({ bezeichnung: bezeichnung.trim(), preisCent, kategorie: kategorie.trim(), groesse: varianten.length ? "" : groesse.trim(), anzahl, bezeichnungen, regalId: regalId || undefined });
      if (vorschau) {
        for (const a of r.artikel) {
          try {
            await foto.mutateAsync({ id: a.id, blob: vorschau.blob });
          } catch (err) {
            setFehler(err instanceof ApiFehler ? `Artikel angelegt, aber das Foto kam nicht an: ${err.message}` : "Artikel angelegt, Foto fehlt.");
          }
        }
      }
      if (einst) {
        const neueKategorien = kategorie.trim() ? [kategorie.trim(), ...einst.kategorien.filter((k) => k !== kategorie.trim())].slice(0, 12) : einst.kategorien;
        const neueVorlagen = alsVorlage && !einst.vorlagen.some((v) => v.bezeichnung === bezeichnung.trim()) ? [{ bezeichnung: bezeichnung.trim(), preisCent, kategorie: kategorie.trim() }, ...einst.vorlagen].slice(0, 20) : einst.vorlagen;
        if (neueKategorien !== einst.kategorien || neueVorlagen !== einst.vorlagen) portal.mutate({ ...einst, kategorien: neueKategorien, vorlagen: neueVorlagen });
      }
      setAngelegt(r.artikel);
      setHeuteZaehler((n) => n + r.artikel.length);
      setSchritt(3);
    } catch (err) {
      if (!(err instanceof ApiFehler) && istNetzfehler(err)) {
        const ok = await offlineMerken({ bezeichnung: bezeichnung.trim(), preisCent, kategorie: kategorie.trim(), groesse: varianten.length ? "" : groesse.trim(), anzahl, bezeichnungen, regalId: regalId || undefined }, vorschau?.blob ?? null);
        if (ok) {
          setAngelegt([]);
          setFehler(null);
          setOfflineHinweis(true);
          setSchritt(3);
          return;
        }
        setFehler("Kein Netz, und die Warteschlange ist voll (20). Bitte später noch einmal.");
        return;
      }
      setFehler(err instanceof ApiFehler ? err.message : "Anlegen hat nicht geklappt.");
    }
  }

  function naechster() {
    setOriginal(null);
    setVorschau(null);
    setBewertung(null);
    setBezeichnung("");
    setPreis("");
    setGroesse("");
    setVarianten([]);
    setAngelegt([]);
    setFehler(null);
    setOfflineHinweis(false);
    setSchritt(1);
  }

  return (
    <Seitenzustand query={info} lines={5}>
      {(d) => (
        <div className="sc-col" style={{ gap: "var(--space-4)" }}>
          <div className="sc-spread">
            <h1 className="title">{schritt === 1 ? t("erfassen.schritt1") : schritt === 2 ? t("erfassen.schritt2") : t("erfassen.schritt3")}</h1>
            <StatusBadge status="paid">{t("start.heute")} {d.heuteErfasst + heuteZaehler}</StatusBadge>
          </div>
          <div className="mp-schrittleiste" role="progressbar" aria-valuemin={1} aria-valuemax={3} aria-valuenow={schritt} aria-label={`Schritt ${schritt} von 3`}>{[1, 2, 3].map((n) => <span key={n} className={n <= schritt ? "is-on" : ""} />)}</div>
          {d.regale.length === 0 ? <Notice tone="attention" title={tx("Noch kein gebuchtes Regal")}>Sobald dein Mietvertrag zugestimmt ist, kannst du hier Artikel erfassen. Frag sonst im Laden nach.</Notice> : null}
          {fehler ? <Notice tone="error">{fehler}</Notice> : null}

          {schritt === 1 ? (
            <>
              <button type="button" className="mp-foto-gross" onClick={() => input.current?.click()} aria-label="Foto aufnehmen" disabled={d.regale.length === 0}>
                <span className="sc-col sc-center" style={{ gap: 8 }}>
                  <Icon name="camera" size={56} />
                  <span className="sc-strong">{t("erfassen.foto")}</span>
                  <span className="sc-muted sc-small">{t("erfassen.fotoHinweis")}</span>
                </span>
              </button>
              <div className="sc-toolbar">
                <Button variant="secondary" icon="image-off" onClick={() => galerie.current?.click()} disabled={d.regale.length === 0}>{t("erfassen.galerie")}</Button>
                <Button variant="quiet" onClick={() => setSchritt(2)} disabled={d.regale.length === 0}>{t("erfassen.ohneFoto")}</Button>
                <Link to="/mieter/sammel" className="rg-btn rg-btn--quiet">{t("kachel.sammel")}</Link>
              </div>
              {vorlagen.length ? (
                <div className="sc-col" style={{ gap: 6 }}>
                  <span className="rg-field__label">Schnell anlegen aus Vorlage</span>
                  <div className="sc-toolbar">
                    {vorlagen.map((v) => <button key={v.bezeichnung} type="button" className="sc-pill" onClick={() => { vorlageNutzen(v); setSchritt(2); }}>{v.bezeichnung} · {formatEuro(v.preisCent)}</button>)}
                  </div>
                </div>
              ) : null}
              <input ref={input} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { void fotoGewaehlt(e.target.files?.[0]); e.target.value = ""; }} />
              <input ref={galerie} type="file" accept="image/*" hidden onChange={(e) => { void fotoGewaehlt(e.target.files?.[0]); e.target.value = ""; }} />
            </>
          ) : null}

          {schritt === 2 ? (
            <>
              {vorschau ? (
                <div className="sc-col" style={{ gap: 8 }}>
                  <div className="sc-row" style={{ alignItems: "flex-start" }}>
                    <img src={vorschau.url} alt="" style={{ width: 120, height: 120, objectFit: "cover", borderRadius: "var(--radius-md)", flex: "none" }} />
                    <div className="sc-col" style={{ gap: 6, flex: 1 }}>
                      <div className="sc-toolbar">
                        <Button variant="secondary" icon="undo-2" aria-label="Drehen" onClick={() => setBearb((b) => ({ ...b, drehung: ((b.drehung ?? 0) + 90) % 360 }))}>Drehen</Button>
                        <button type="button" className="sc-pill" aria-pressed={!!bearb.quadrat} onClick={() => setBearb((b) => ({ ...b, quadrat: !b.quadrat }))}>Quadrat</button>
                        <Button variant="quiet" icon="camera" onClick={() => setSchritt(1)}>Neu</Button>
                      </div>
                      <label className="sc-small">Helligkeit <input type="range" min={0.6} max={1.6} step={0.05} value={bearb.helligkeit ?? 1} onChange={(e) => setBearb((b) => ({ ...b, helligkeit: Number(e.target.value) }))} style={{ width: "100%" }} /></label>
                      {bearb.quadrat ? <label className="sc-small">Ausschnitt <input type="range" min={-1} max={1} step={0.05} value={bearb.versatzX ?? 0} onChange={(e) => setBearb((b) => ({ ...b, versatzX: Number(e.target.value), versatzY: Number(e.target.value) }))} style={{ width: "100%" }} /></label> : null}
                    </div>
                  </div>
                  {bewertung?.dunkel || bewertung?.unscharf ? <Notice tone="attention" icon="camera">{bewertung.dunkel ? "Das Foto ist ziemlich dunkel – Helligkeit ist schon angehoben, besser bei Tageslicht neu machen." : "Das Foto wirkt unscharf – kurz still halten und neu aufnehmen."}</Notice> : null}
                </div>
              ) : (
                <Button variant="quiet" icon="camera" onClick={() => setSchritt(1)} style={{ alignSelf: "flex-start" }}>Doch ein Foto machen</Button>
              )}
              {d.regale.length > 1 ? (
                <Select label="Regal" value={regalId} onChange={(e) => setRegalId(e.target.value)}>
                  {d.regale.map((r) => <option key={r.regalId} value={r.regalId}>Regal {r.nummer}{r.lage ? ` · ${r.lage}` : ""}</option>)}
                </Select>
              ) : null}
              {(() => { const treffer = (d.verboteneWaren ?? []).filter((w) => `${bezeichnung} ${kategorie}`.toLowerCase().includes(w.toLowerCase())); return treffer.length ? <Notice tone="attention" title={tx("Bitte vorher mit dem Laden klären")}>Der Laden nimmt „{treffer.join("“, „")}“ normalerweise nicht an. Du kannst den Artikel trotzdem anlegen – frag kurz nach.</Notice> : null; })()}
              <TextField id="mp-bezeichnung" label={t("erfassen.bezeichnung")} value={bezeichnung} onChange={(e) => setBezeichnung(e.target.value)} placeholder="z. B. Wollschal, grau" maxLength={80} autoFocus />
              {kategorien.length || kategorie ? (
                <div className="sc-col" style={{ gap: 6 }}>
                  <span className="rg-field__label">Kategorie <span className="sc-muted">(hilft bei Preis und Statistik)</span></span>
                  <div className="sc-toolbar">
                    {kategorien.map((k) => <button key={k} type="button" className="sc-pill" aria-pressed={kategorie === k} onClick={() => setKategorie(kategorie === k ? "" : k)}>{k}</button>)}
                    <button type="button" className="sc-pill" onClick={() => setMehr(true)}>Andere …</button>
                  </div>
                </div>
              ) : null}
              <TextField label={t("erfassen.preis")} value={preis} onChange={(e) => setPreis(e.target.value)} inputMode="decimal" suffix="€" placeholder="0,00" error={(() => { const g = (d.kategorieGrenzen ?? []).find((x) => x.kategorie.toLowerCase() === kategorie.trim().toLowerCase()); return preisHinweis(preisCent, g?.minCent ?? d.preisMinCent, g?.maxCent ?? d.preisMaxCent) ?? undefined; })()} />
              <PreisChips chips={d.preisChipsCent} wertCent={preisCent} onWahl={(c) => setPreis((c / 100).toFixed(2).replace(".", ","))} />
              <Preisspanne kategorie={kategorie} wort={bezeichnung} preisCent={preisCent} onVorschlag={(c) => setPreis((c / 100).toFixed(2).replace(".", ","))} />
              <div className="sc-col" style={{ gap: 6 }}>
                <span className="rg-field__label">Mehrere Größen? <span className="sc-muted">(je Größe ein Artikel)</span></span>
                <div className="sc-toolbar">
                  {GROESSEN.map((g) => <button key={g} type="button" className="sc-pill" aria-pressed={varianten.includes(g)} onClick={() => setVarianten((v) => (v.includes(g) ? v.filter((x) => x !== g) : [...v, g]))}>{g}</button>)}
                  {varianten.length ? <span className="sc-muted sc-small">{varianten.length} Artikel à {preisCent ? formatEuro(preisCent) : "–"}</span> : null}
                </div>
              </div>
              <button type="button" className="rg-btn rg-btn--quiet" style={{ justifyContent: "flex-start", paddingLeft: 0 }} onClick={() => setMehr(!mehr)} aria-expanded={mehr}>
                <Icon name={mehr ? "minus" : "chevron-right"} size={18} /> Größe und Kategorie von Hand
              </button>
              {mehr ? (
                <div className="sc-form">
                  <TextField label="Größe" value={groesse} onChange={(e) => setGroesse(e.target.value)} placeholder="z. B. Gr. 38, 20 × 30 cm" maxLength={40} disabled={varianten.length > 0} />
                  <TextField label="Kategorie" value={kategorie} onChange={(e) => setKategorie(e.target.value)} placeholder="z. B. Deko, Bücher" maxLength={40} />
                </div>
              ) : null}
              <div className="mp-foot">
                <Button variant="primary" size="lg" block icon="check" loading={anlegen.isPending || foto.isPending} disabled={d.regale.length === 0} onClick={() => void speichern(false)}>{varianten.length > 1 ? `${varianten.length} Artikel anlegen` : "Artikel anlegen"}</Button>
                <Button variant="quiet" block icon="copy" disabled={anlegen.isPending} onClick={() => void speichern(true)}>Anlegen und als Vorlage merken</Button>
              </div>
            </>
          ) : null}

          {schritt === 3 && offlineHinweis ? (
            <>
              <Notice tone="attention" icon="clock" title={tx("Offline gespeichert")}>{t("erfassen.offline")}</Notice>
              <Button variant="primary" size="lg" block icon="plus" onClick={naechster}>{t("erfassen.naechster")}</Button>
            </>
          ) : null}
          {schritt === 3 && angelegt.length ? (
            <>
              <Notice tone="joy" icon="sparkles" title={angelegt.length === 1 ? `${angelegt[0]!.bezeichnung} ist angelegt` : `${angelegt.length} Artikel sind angelegt`}>
                {angelegt.map((a) => a.code).join(", ")} · {formatEuro(angelegt[0]!.preisCent)} · Regal {angelegt[0]!.regalNummer}
              </Notice>
              {vorschau ? <img src={vorschau.url} alt="" style={{ width: "100%", maxWidth: 240, borderRadius: "var(--radius-lg)" }} /> : null}
              <div className="mp-foot" style={{ position: "static", margin: 0, borderRadius: "var(--radius-lg)" }}>
                <Button variant="primary" size="lg" block icon="plus" onClick={naechster}>{tx("Nächster Artikel")}</Button>
                <div className="sc-toolbar">
                  <Link to={`/mieter/etiketten?ids=${angelegt.map((a) => a.id).join(",")}`} className="rg-btn rg-btn--secondary">Etikett jetzt drucken</Link>
                  <Link to={`/mieter/artikel/${angelegt[0]!.id}/kopieren`} className="rg-btn rg-btn--quiet">Ähnlichen kopieren</Link>
                  <Button variant="quiet" onClick={() => navigate("/mieter/packliste")}>Zur Einräum-Liste</Button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      )}
    </Seitenzustand>
  );
}
